import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const InventoryAuditModal = ({ isOpen, onClose, onStartAudit }) => {
  const [auditSettings, setAuditSettings] = useState({
    auditType: 'full',
    locations: [],
    designs: [],
    includeReserved: true,
    includeDefective: false,
    auditBy: '',
    scheduledDate: '',
    notes: ''
  });

  const auditTypeOptions = [
    { value: 'full', label: 'Full Inventory Audit' },
    { value: 'cycle', label: 'Cycle Count Audit' },
    { value: 'spot', label: 'Spot Check Audit' },
    { value: 'location', label: 'Location-based Audit' }
  ];

  const locationOptions = [
    { value: 'warehouse-a', label: 'Warehouse A' },
    { value: 'warehouse-b', label: 'Warehouse B' },
    { value: 'production-floor', label: 'Production Floor' },
    { value: 'quality-check', label: 'Quality Check Area' },
    { value: 'dispatch', label: 'Dispatch Section' }
  ];

  const designOptions = [
    { value: 'floral-print', label: 'Floral Print' },
    { value: 'geometric', label: 'Geometric Pattern' },
    { value: 'abstract', label: 'Abstract Design' },
    { value: 'traditional', label: 'Traditional Motif' },
    { value: 'modern', label: 'Modern Art' }
  ];

  const handleStartAudit = () => {
    onStartAudit(auditSettings);
    onClose();
  };

  const handleInputChange = (field, value) => {
    setAuditSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleMultiSelectChange = (field, value) => {
    setAuditSettings(prev => ({
      ...prev,
      [field]: Array.isArray(value) ? value : [value]
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-1001">
      <div className="bg-card border border-border rounded-lg w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
              <Icon name="ClipboardCheck" size={20} color="white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Inventory Audit</h2>
              <p className="text-sm text-muted-foreground">
                Configure and start a new inventory audit process
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            iconName="X"
            iconSize={20}
          />
        </div>

        <div className="p-6 space-y-6">
          {/* Audit Type */}
          <div>
            <Select
              label="Audit Type"
              description="Select the type of inventory audit to perform"
              options={auditTypeOptions}
              value={auditSettings.auditType}
              onChange={(value) => handleInputChange('auditType', value)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Locations */}
            <div>
              <Select
                label="Locations to Audit"
                description="Select specific locations (leave empty for all)"
                options={locationOptions}
                value={auditSettings.locations}
                onChange={(value) => handleMultiSelectChange('locations', value)}
                multiple
                searchable
              />
            </div>

            {/* Designs */}
            <div>
              <Select
                label="Design Categories"
                description="Select specific designs (leave empty for all)"
                options={designOptions}
                value={auditSettings.designs}
                onChange={(value) => handleMultiSelectChange('designs', value)}
                multiple
                searchable
              />
            </div>
          </div>

          {/* Audit Options */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-foreground">Audit Options</label>
            <div className="space-y-2">
              <Checkbox
                label="Include reserved stock in audit"
                checked={auditSettings.includeReserved}
                onChange={(e) => handleInputChange('includeReserved', e.target.checked)}
              />
              <Checkbox
                label="Include defective pieces in audit"
                checked={auditSettings.includeDefective}
                onChange={(e) => handleInputChange('includeDefective', e.target.checked)}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Audit By */}
            <div>
              <Input
                label="Audit Conducted By"
                type="text"
                description="Name of the person conducting the audit"
                value={auditSettings.auditBy}
                onChange={(e) => handleInputChange('auditBy', e.target.value)}
                placeholder="Enter auditor name"
                required
              />
            </div>

            {/* Scheduled Date */}
            <div>
              <Input
                label="Scheduled Date"
                type="date"
                description="When should this audit be conducted"
                value={auditSettings.scheduledDate}
                onChange={(e) => handleInputChange('scheduledDate', e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                required
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <Input
              label="Audit Notes"
              type="text"
              description="Additional notes or instructions for the audit"
              value={auditSettings.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Enter any special instructions or notes..."
            />
          </div>

          {/* Audit Summary */}
          <div className="bg-muted/30 rounded-lg p-4">
            <h3 className="font-medium text-foreground mb-3">Audit Summary</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Audit Type:</span>
                  <span className="text-foreground font-medium">
                    {auditTypeOptions.find(type => type.value === auditSettings.auditType)?.label}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Locations:</span>
                  <span className="text-foreground font-medium">
                    {auditSettings.locations.length === 0 ? 'All Locations' : `${auditSettings.locations.length} Selected`}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Designs:</span>
                  <span className="text-foreground font-medium">
                    {auditSettings.designs.length === 0 ? 'All Designs' : `${auditSettings.designs.length} Selected`}
                  </span>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Include Reserved:</span>
                  <span className="text-foreground font-medium">
                    {auditSettings.includeReserved ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Include Defective:</span>
                  <span className="text-foreground font-medium">
                    {auditSettings.includeDefective ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Scheduled Date:</span>
                  <span className="text-foreground font-medium">
                    {auditSettings.scheduledDate || 'Not set'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Estimated Items */}
          <div className="bg-primary/10 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="Info" size={16} className="text-primary" />
              <span className="text-sm font-medium text-primary">Estimated Audit Scope</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Based on your selections, this audit will cover approximately <strong>2,450 items</strong> across <strong>15 design categories</strong> in <strong>3 locations</strong>. 
              Estimated completion time: <strong>4-6 hours</strong>.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between p-6 border-t border-border bg-muted/20">
          <div className="text-sm text-muted-foreground">
            Audit data will be saved and can be resumed if interrupted
          </div>
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleStartAudit}
              disabled={!auditSettings.auditBy || !auditSettings.scheduledDate}
              iconName="ClipboardCheck"
              iconPosition="left"
              iconSize={16}
            >
              Start Audit
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InventoryAuditModal;